// components/TestimonialsSectionClient.tsx
'use client';

import clsx from 'clsx';
import { LazyMotion, domAnimation, m } from 'framer-motion';
import ButtonLandingPage from '@/components/landing/ButtonLandingPage';
import CtaButton from '@/components/ui/CtaButton';
import Underline from '@/components/ui/underline';
import type { ReactNode } from 'react';

export type TestimonialsSectionTheme = 'light' | 'dark';

const slideUp = {
    hidden: { y: 40, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

const headerVariants = {
    hidden: {},
    visible: {
        transition: { staggerChildren: 0.1 },
    },
} as const;

type Props = {
    heading: string;
    tagline: string;
    hasItems: boolean;
    theme?: TestimonialsSectionTheme;
    useLandingButton?: boolean;
    children: ReactNode;
};

export default function TestimonialsSectionClient({
    heading,
    tagline,
    hasItems,
    theme = 'light',
    useLandingButton = false,
    children,
}: Props) {
    const isDark = theme === 'dark';

    return (
        <LazyMotion features={domAnimation}>
            <section
                className={clsx(
                    'overflow-x-clip py-16 md:py-24 lg:py-28',
                    isDark && 'rounded-t-3xl bg-secondary',
                )}
            >
                <m.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    variants={headerVariants}
                    className={clsx(
                        'container px-[5%] mb-12 md:mb-16 lg:mb-20',
                        isDark && 'text-primary',
                    )}
                >
                    <div className="mx-auto max-w-lg text-center">
                        {tagline && (
                            <div className="relative inline-block mb-2 md:mb-6">
                                <m.p variants={slideUp} custom={0} className="uppercase tracking-[0.10em] text-sm">
                                    {tagline}
                                </m.p>
                                <Underline
                                    delay={0.8}
                                    useWhileInView
                                    className="pointer-events-none absolute -left-2 top-full -mt-1 text-accent h-5 w-[170px] md:h-6 md:w-[210px]"
                                />
                            </div>
                        )}

                        {heading && (
                            <m.h2
                                variants={slideUp}
                                custom={1}
                                className="mb-6 pt-6 text-3xl font-medium leading-tight md:text-7xl"
                            >
                                {heading}
                            </m.h2>
                        )}

                        <m.div variants={slideUp} custom={2} className="mt-8 flex justify-center">
                            {useLandingButton ? <ButtonLandingPage /> : <CtaButton />}
                        </m.div>
                    </div>
                </m.div>

                {/* Marquee: tylko CSS — bez Framer Motion (unika konfliktu transform/opacity) */}
                {hasItems ? (
                    <div className="w-full overflow-x-clip">
                        <div className="flex w-max motion-safe:animate-loop-testimonials motion-safe:hover:[animation-play-state:paused]">
                            {children}
                        </div>
                    </div>
                ) : null}
            </section>
        </LazyMotion>
    );
}
